import React, { useEffect, useState } from "react"
import { FormControlLabel, Switch, Typography } from "@material-ui/core"
import { useNotify, useRecordContext } from "react-admin"
import firebase, { auth, db } from "../firebase"

interface UnsubscribeState {
  unsubscribed: boolean
  at?: Date
  source?: string
}

/**
 * Shows and toggles whether a claimant receives emails at all.
 *
 * The flag lives on the claimant (`users/{uid}.emailUnsubscribed`),
 * so it can be used on user pages (record.id) and claim pages
 * (record.userId) alike, and changes are saved immediately.
 */
export default function EmailsToggle (
  { userIdSource = "userId" }: { userIdSource?: "userId" | "id" },
) {
  const record = useRecordContext()
  const userId = record?.[userIdSource] as string | undefined
  const notify = useNotify()
  const [state, setState] = useState<UnsubscribeState | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!userId) return

    return db.doc(`users/${userId}`).onSnapshot((snap) => {
      const data = snap.data() ?? {}
      setState({
        unsubscribed: data.emailUnsubscribed === true,
        at: data.emailUnsubscribedAt?.toDate?.(),
        source: data.emailUnsubscribedSource,
      })
    })
  }, [userId])

  if (!userId || !state) return null

  async function handleChange (event: React.ChangeEvent<HTMLInputElement>) {
    const receivesEmails = event.target.checked
    setSaving(true)

    try {
      await db.doc(`users/${userId}`).update({
        emailUnsubscribed: !receivesEmails,
        emailUnsubscribedAt: receivesEmails
          ? null
          : firebase.firestore.FieldValue.serverTimestamp(),
        emailUnsubscribedSource: receivesEmails ? null : "admin",
        updatedBy: "admin-dashboard",
        updatedById: auth.currentUser?.uid ?? null,
      })
      notify(receivesEmails
        ? "Claimant will receive emails again"
        : "Claimant unsubscribed from all emails")
    }
    catch (error) {
      console.error(error)
      notify("Could not update email setting", "warning")
    }
    finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <FormControlLabel
        control={
          <Switch
            checked={!state.unsubscribed}
            onChange={handleChange}
            disabled={saving}
            color='primary'
          />
        }
        label='Receives emails'
      />
      {state.unsubscribed &&
        <Typography variant='caption' display='block' color='textSecondary'>
          Unsubscribed
          {state.at ? ` on ${state.at.toLocaleString("sv-SE")}` : ""}
          {state.source ? ` via ${state.source}` : ""}
        </Typography>
      }
    </div>
  )
}
